import { useEffect, useState } from 'react';

import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from './useDesignerUIState';

type StepState = 'pending' | 'loading' | 'completed';

export function useLoaderState(gameId?: string | null) {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const loaderState = useDesignerUIContext(
    context => (gameId ? context.state.loaderStates[gameId] : undefined),
  );
  
  // Use local state to trigger re-renders every 500ms
  const [, setTick] = useState(0);

  // Initialize state if gameId exists and state doesn't
  useEffect(() => {
    if (!gameId || loaderState) return;
    
    dispatch({
      type: DesignerUIStateActions.initLoaderState,
      payload: { gameId },
    });
  }, [gameId, loaderState, dispatch]);

  // Trigger re-renders every 500ms to update the calculated state
  useEffect(() => {
    if (!gameId || !loaderState) return;
    
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [gameId, loaderState]);

  // Return default state if not initialized yet
  if (!loaderState) {
    return {
      stepStates: ['pending', 'pending', 'pending', 'pending'] as StepState[],
      showPin: false,
    };
  }

  // Calculate current state based on elapsed time (pure calculation!)
  const elapsed = Date.now() - loaderState.startTime;
  
  let stepStates: StepState[];
  let showPin = false;

  if (elapsed < 4000) {
    // Step 1: First 4 seconds
    stepStates = ['loading', 'pending', 'pending', 'pending'];
    showPin = false;
  } else if (elapsed < 8000) {
    // Step 2: 4-8 seconds
    stepStates = ['completed', 'loading', 'pending', 'pending'];
    showPin = true;
  } else if (elapsed < 12000) {
    // Step 3: 8-12 seconds
    stepStates = ['completed', 'completed', 'loading', 'pending'];
    showPin = true;
  } else {
    // Step 4: After 12 seconds
    stepStates = ['completed', 'completed', 'completed', 'loading'];
    showPin = true;
  }

  return { stepStates, showPin };
}
